/**
 * <s-product-gallery> — one-large-image-at-a-time product gallery.
 *
 * Wraps the horizontal scroll-snap track `[data-product-gallery]` (one slide per
 * view) and wires:
 *   [data-gallery-prev] / [data-gallery-next]  — arrow buttons inside the element
 *   [data-gallery-thumb="<index>"]             — thumbnail buttons in the sibling
 *                                                `[data-gallery-thumbs]` strip
 * The current slide is derived from scroll position, so swipe, arrows and
 * thumbnails stay in sync; the active thumbnail gets aria-current="true".
 */
class SProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    const thumbStrip = this.parentElement?.querySelector('[data-gallery-thumbs]');
    this.thumbs = thumbStrip ? [...thumbStrip.querySelectorAll('[data-gallery-thumb]')] : [];
    this.slides = [...this.track.children];
    this.index = 0;

    this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
    this.next?.addEventListener('click', () => this.goTo(this.index + 1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)));
    });

    // Natural galleries (photos keep their own shapes): the track's height hugs
    // the active slide instead of staying as tall as the tallest one.
    if (this.track.hasAttribute('data-gallery-natural')) {
      this.fitToActiveSlide = () => {
        const slide = this.slides[this.index];
        if (!slide) return;
        if (this.observedSlide !== slide) {
          if (this.observedSlide) this.slideObserver.unobserve(this.observedSlide);
          this.slideObserver.observe(slide);
          this.observedSlide = slide;
        }
        this.track.style.height = `${slide.offsetHeight}px`;
      };
      this.slideObserver = new ResizeObserver(() => this.fitToActiveSlide());
      this.observedSlide = null;
      this.onResize = () => this.fitToActiveSlide();
      window.addEventListener('resize', this.onResize);
      this.fitToActiveSlide();
    }

    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.sync());
      },
      { passive: true },
    );

    this.sync();

    // Variant photos: when a shopper picks an option whose variant has its own photo, slide to it.
    try {
      this.variantMedia = JSON.parse(this.querySelector('[data-variant-media]')?.textContent || '{}');
    } catch {
      this.variantMedia = {};
    }
    this.onVariantChange = (event) => {
      const input = event.target;
      const variantId = input?.dataset?.variantId || input?.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.showVariantMedia(variantId);
    };
    document.addEventListener('change', this.onVariantChange);
    // Deep links (?variant=…) open on that variant's photo.
    if (new URLSearchParams(location.search).has('variant')) {
      this.showVariantMedia(this.dataset.selectedVariantId);
    }
  }

  showVariantMedia(variantId) {
    const mediaId = this.variantMedia?.[variantId];
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0 && index !== this.index) this.goTo(index);
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    // Slides are each one track-width wide; negative offsets scroll RTL tracks.
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const offset = clamped * this.track.clientWidth * (isRtl ? -1 : 1);
    this.track.scrollTo({ left: offset });
    this.setIndex(clamped);
  }

  sync() {
    const width = this.track.clientWidth || 1;
    this.setIndex(Math.round(Math.abs(this.track.scrollLeft) / width));
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onVariantChange);
    if (this.fitToActiveSlide) {
      window.removeEventListener('resize', this.onResize);
      this.slideObserver.disconnect();
    }
  }

  setIndex(index) {
    this.index = Math.max(0, Math.min(index, this.slides.length - 1));
    this.fitToActiveSlide?.();
    if (this.prev) this.prev.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    this.thumbs.forEach((thumb, i) => {
      if (i === this.index) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumbnail in view by scrolling the strip only, never the page.
        const strip = thumb.parentElement;
        const thumbStart = thumb.offsetLeft - strip.offsetLeft;
        if (thumbStart < strip.scrollLeft || thumbStart + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: thumbStart - 8 });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
